import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

export interface HelicopterHandle {
  group: THREE.Group;
  update: (dt: number, playerX: number, playerZ: number, active: boolean) => void;
  setActive: (active: boolean) => void;
  isActive: () => boolean;
  getPosition: () => THREE.Vector3;
}

const HELICOPTER_CONFIG = {
  height: 18, // VERY LOW - always visible
  followSpeed: 3.0,
  orbitRadius: 20, // VERY CLOSE to player
  orbitSpeed: 0.6, // Fast orbit
  spotlightIntensity: 25.0, // Very bright spotlight
  spotlightAngle: Math.PI / 3,
  spotlightDistance: 80,
  rotorSpeed: 35,
  scale: 0.1,
  // Swoop behavior
  swoopChance: 0.012, // More frequent swoops
  swoopDuration: 2.5, // How long a swoop lasts
  swoopHeight: 10, // How low it dives (VERY LOW)
  swoopSpeed: 3.0, // How fast it moves during swoop
};

export async function createHelicopter(scene: THREE.Scene): Promise<HelicopterHandle> {
  const group = new THREE.Group();
  group.name = 'PoliceHelicopter';
  group.visible = false;
  
  let helicopterModel: THREE.Group | null = null;
  let mainRotor: THREE.Object3D | null = null;
  let tailRotor: THREE.Object3D | null = null;
  let active = false;
  let orbitAngle = 0;
  let currentX = 0;
  let currentZ = 0;
  let currentHeight = HELICOPTER_CONFIG.height;
  
  // Swoop state
  let isSwooping = false;
  let swoopTimer = 0;
  let swoopStartX = 0;
  let swoopStartZ = 0;
  let swoopTargetX = 0;
  let swoopTargetZ = 0;
  let lastPlayerVelX = 0;
  let lastPlayerVelZ = 0;
  let lastPlayerX = 0;
  let lastPlayerZ = 0;
  
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
  
  const loader = new GLTFLoader();
  loader.setDRACOLoader(dracoLoader);
  
  try {
    const gltf = await loader.loadAsync('/assets/models/hind_attack_helicopter.glb');
    helicopterModel = gltf.scene;
    
    // Log the model's bounding box to understand its size
    const box = new THREE.Box3().setFromObject(helicopterModel);
    const size = box.getSize(new THREE.Vector3());
    console.log('Helicopter model loaded, original size:', size.x.toFixed(1), size.y.toFixed(1), size.z.toFixed(1));
    
    // Auto-scale to reasonable size (target ~10 units long)
    const maxDim = Math.max(size.x, size.y, size.z);
    const autoScale = 10 / maxDim;
    helicopterModel.scale.setScalar(autoScale);
    console.log('Helicopter auto-scaled to:', autoScale.toFixed(4), '(final size ~10 units)');
    
    helicopterModel.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.material) {
          const mat = child.material as THREE.MeshStandardMaterial;
          mat.roughness = 0.4;
          mat.metalness = 0.6;
          mat.envMapIntensity = 1.0;
        }
      }
      
      const name = child.name.toLowerCase();
      if (name.includes('rotor') && name.includes('main') || name.includes('blade') && !name.includes('tail')) {
        mainRotor = child;
      }
      if (name.includes('tail') && (name.includes('rotor') || name.includes('blade'))) {
        tailRotor = child;
      }
    });
    
    if (!mainRotor) {
      helicopterModel.traverse((child) => {
        if (child.name.toLowerCase().includes('rotor') && !mainRotor) {
          mainRotor = child;
        }
      });
    }
    
    group.add(helicopterModel);
  } catch (err) {
    console.warn('Failed to load helicopter model, using placeholder:', err);
    
    // Fallback placeholder helicopter
    const bodyGeo = new THREE.CylinderGeometry(1.5, 2, 8, 8);
    bodyGeo.rotateZ(Math.PI / 2);
    const bodyMat = new THREE.MeshStandardMaterial({ color: 0x1a1a2a, metalness: 0.7, roughness: 0.3 });
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    group.add(body);
    
    const rotorGeo = new THREE.BoxGeometry(12, 0.1, 0.3);
    const rotorMat = new THREE.MeshStandardMaterial({ color: 0x333333 });
    mainRotor = new THREE.Mesh(rotorGeo, rotorMat);
    mainRotor.position.y = 2;
    group.add(mainRotor);
    
    const tailGeo = new THREE.CylinderGeometry(0.3, 0.5, 6, 6);
    tailGeo.rotateZ(Math.PI / 2);
    const tail = new THREE.Mesh(tailGeo, bodyMat);
    tail.position.x = -5;
    group.add(tail);
  }
  
  // Emissive spotlight cone mesh instead of real SpotLight (huge perf savings)
  const coneMat = new THREE.MeshBasicMaterial({
    color: 0xffffcc,
    transparent: true,
    opacity: 0.08,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const spotCone = new THREE.Mesh(
    new THREE.ConeGeometry(6, HELICOPTER_CONFIG.height * 0.6, 8, 1, true),
    coneMat,
  );
  spotCone.position.y = -HELICOPTER_CONFIG.height * 0.3;
  spotCone.rotation.x = Math.PI;
  group.add(spotCone);

  // Police lights - emissive meshes (no PointLights)
  const redGlow = new THREE.Mesh(
    new THREE.SphereGeometry(0.4, 4, 4),
    new THREE.MeshBasicMaterial({ color: 0xff0000 }),
  );
  redGlow.position.set(0, 2, -2);
  group.add(redGlow);

  const blueGlow = new THREE.Mesh(
    new THREE.SphereGeometry(0.4, 4, 4),
    new THREE.MeshBasicMaterial({ color: 0x0066ff }),
  );
  blueGlow.position.set(0, 2, 2);
  group.add(blueGlow);
  
  let lightFlashTimer = 0;
  
  scene.add(group);
  
  return {
    group,
    
    update(dt: number, playerX: number, playerZ: number, shouldBeActive: boolean) {
      if (shouldBeActive && !active) {
        active = true;
        group.visible = true;
        currentX = playerX + 30;
        currentZ = playerZ + 30;
        currentHeight = HELICOPTER_CONFIG.height;
        orbitAngle = Math.random() * Math.PI * 2;
        isSwooping = false;
        console.log('🚁 Helicopter ACTIVATED!');
      } else if (!shouldBeActive && active) {
        active = false;
        group.visible = false;
      }
      
      if (!active) return;
      
      // Estimate player velocity for swoop targeting
      const playerVelX = (playerX - lastPlayerX) / Math.max(dt, 0.001);
      const playerVelZ = (playerZ - lastPlayerZ) / Math.max(dt, 0.001);
      lastPlayerVelX = lastPlayerVelX * 0.9 + playerVelX * 0.1;
      lastPlayerVelZ = lastPlayerVelZ * 0.9 + playerVelZ * 0.1;
      lastPlayerX = playerX;
      lastPlayerZ = playerZ;
      
      const playerSpeed = Math.hypot(lastPlayerVelX, lastPlayerVelZ);
      
      // Check if we should start a swoop
      if (!isSwooping && playerSpeed > 15 && Math.random() < HELICOPTER_CONFIG.swoopChance) {
        isSwooping = true;
        swoopTimer = 0;
        swoopStartX = currentX;
        swoopStartZ = currentZ;
        // Target position: in front of the player's path
        const velDir = Math.atan2(lastPlayerVelZ, lastPlayerVelX);
        const swoopDist = 40 + Math.random() * 20;
        swoopTargetX = playerX + Math.cos(velDir) * swoopDist;
        swoopTargetZ = playerZ + Math.sin(velDir) * swoopDist;
        console.log('🚁 Helicopter SWOOPING!');
      }
      
      let targetX: number;
      let targetZ: number;
      let targetHeight: number;
      
      if (isSwooping) {
        swoopTimer += dt;
        const swoopProgress = swoopTimer / HELICOPTER_CONFIG.swoopDuration;
        
        if (swoopProgress >= 1) {
          isSwooping = false;
        } else {
          // Swoop path: dive down, cross in front, pull up
          const swoopCurve = Math.sin(swoopProgress * Math.PI); // 0 -> 1 -> 0
          
          // Interpolate position
          targetX = swoopStartX + (swoopTargetX - swoopStartX) * swoopProgress;
          targetZ = swoopStartZ + (swoopTargetZ - swoopStartZ) * swoopProgress;
          
          // Height dips during middle of swoop
          targetHeight = HELICOPTER_CONFIG.height - swoopCurve * (HELICOPTER_CONFIG.height - HELICOPTER_CONFIG.swoopHeight);
          
          // Fast movement during swoop
          const swoopLerp = 1 - Math.exp(-HELICOPTER_CONFIG.swoopSpeed * dt);
          currentX += (targetX - currentX) * swoopLerp;
          currentZ += (targetZ - currentZ) * swoopLerp;
          currentHeight += (targetHeight - currentHeight) * swoopLerp;
        }
      }
      
      if (!isSwooping) {
        // Normal orbit around player
        targetX = playerX + Math.cos(orbitAngle) * HELICOPTER_CONFIG.orbitRadius;
        targetZ = playerZ + Math.sin(orbitAngle) * HELICOPTER_CONFIG.orbitRadius;
        targetHeight = HELICOPTER_CONFIG.height;
        
        orbitAngle += HELICOPTER_CONFIG.orbitSpeed * dt;
        
        // Smooth follow
        const lerpFactor = 1 - Math.exp(-HELICOPTER_CONFIG.followSpeed * dt);
        currentX += (targetX - currentX) * lerpFactor;
        currentZ += (targetZ - currentZ) * lerpFactor;
        currentHeight += (targetHeight - currentHeight) * lerpFactor * 0.5;
      }
      
      group.position.set(currentX, currentHeight, currentZ);
      
      // Face the player (or swoop direction during swoop)
      let lookAngle: number;
      if (isSwooping) {
        lookAngle = Math.atan2(swoopTargetZ - currentZ, swoopTargetX - currentX);
      } else {
        lookAngle = Math.atan2(playerZ - currentZ, playerX - currentX);
      }
      group.rotation.y = lookAngle + Math.PI / 2;
      
      // Banking - more aggressive during swoop
      const bankIntensity = isSwooping ? 0.2 : 0.08;
      const bankAngle = Math.sin(orbitAngle * 2) * bankIntensity;
      group.rotation.z = bankAngle;
      
      // Pitch - nose down during swoop dive
      const pitchAngle = isSwooping ? Math.sin(swoopTimer / HELICOPTER_CONFIG.swoopDuration * Math.PI) * 0.15 : 0.03;
      group.rotation.x = pitchAngle;
      
      // Spin rotors
      if (mainRotor) {
        mainRotor.rotation.y += HELICOPTER_CONFIG.rotorSpeed * dt;
      }
      if (tailRotor) {
        tailRotor.rotation.x += HELICOPTER_CONFIG.rotorSpeed * 1.5 * dt;
      }
      
      // Spotlight cone tracks player direction
      const toPlayerX = playerX - currentX;
      const toPlayerZ = playerZ - currentZ;
      const toPlayerAngle = Math.atan2(toPlayerX, toPlayerZ);
      spotCone.rotation.z = (toPlayerAngle - group.rotation.y) * 0.1;
      
      // Flash police lights via visibility toggle
      lightFlashTimer += dt * 5;
      const flash = Math.sin(lightFlashTimer) > 0;
      redGlow.visible = flash;
      blueGlow.visible = !flash;
    },
    
    setActive(newActive: boolean) {
      active = newActive;
      group.visible = newActive;
    },
    
    isActive() {
      return active;
    },
    
    getPosition() {
      return group.position.clone();
    },
  };
}
